import { Wand2, SlidersHorizontal, Shuffle, BarChart3, Rocket } from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Reveal } from "./Reveal";

const PILLARS = [
  {
    tone: "candy",
    icon: Wand2,
    kicker: "CREATE",
    title: "Create",
    body: "Short links, branded QR codes, and full link-in-bio pages — with custom aliases and live link previews.",
    points: ["Custom aliases", "QR on every link", "Bio pages + themes"],
  },
  {
    tone: "pink",
    icon: SlidersHorizontal,
    kicker: "MANAGE",
    title: "Manage",
    body: "Organize everything from one dashboard — tags, campaigns, bulk tools, and link protection.",
    points: ["Tags & campaigns", "Bulk + CSV tools", "Passwords, limits, schedules"],
  },
  {
    tone: "yellow",
    icon: Shuffle,
    kicker: "ROUTE",
    title: "Route",
    body: "One link, infinite paths. Send visitors where they belong by geo, device, A/B variant, time, or rotation — or stop them with a branded CTA first.",
    points: ["Geo & device routing", "A/B testing", "CTA overlays + pixels"],
  },
  {
    tone: "mint",
    icon: BarChart3,
    kicker: "ANALYZE",
    title: "Analyze",
    body: "Understand every click — devices, countries, referrers, unique visitors, heatmaps, and live feeds.",
    points: ["Real-time feed", "Click heatmaps", "Bot filtering + CSV export"],
  },
  {
    tone: "candy",
    icon: Rocket,
    kicker: "SCALE",
    title: "Scale",
    body: "Grow with custom domains, a REST API, webhooks, integrations, and team workspaces.",
    points: ["Custom domains + API", "Webhooks & Slack/Discord", "Team seats"],
  },
];

const dot = { candy: "bg-candy", pink: "bg-pink", yellow: "bg-yellow", mint: "bg-mint" };
const shadow = {
  candy: "shadow-sticker-candy",
  pink: "shadow-sticker-pink",
  yellow: "shadow-sticker-yellow",
  mint: "shadow-sticker-mint",
};

/** PlatformPillars — the product told through outcomes, not feature lists. */
export default function PlatformPillars() {
  return (
    <section
      className="mx-auto max-w-[1400px] px-5 pb-16 pt-20 md:px-10"
      data-testid="pillars-section"
    >
      <Reveal className="mb-12 max-w-2xl">
        <GeometricBadge tone="candy">THE PLATFORM</GeometricBadge>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-6xl">
          Not a shortener.
          <span className="ai-gradient-text"> A whole workflow.</span>
        </h2>
        <p className="mt-4 font-sans text-lg leading-relaxed text-muted-foreground">
          Everything a link can do — from the moment you create it to the day it scales across your
          whole brand.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-5">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 80}>
            <div
              className={`h-full border-2 border-ink ${shadow[p.tone]} sticker-hover flex flex-col gap-4 rounded-3xl bg-white p-6`}
            >
              <FloatingIcon icon={p.icon} tone={p.tone} size={48} shape="square" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                  {p.kicker}
                </div>
                <h3 className="mt-1 font-display text-2xl font-extrabold leading-tight">
                  {p.title}
                </h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-muted-foreground">
                  {p.body}
                </p>
              </div>
              <ul className="mt-auto flex flex-col gap-1.5">
                {p.points.map((pt) => (
                  <li
                    key={pt}
                    className="flex items-center gap-2 font-sans text-[13px] text-ink/75"
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${dot[p.tone]} shrink-0`} />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
